import { useState, useMemo, useEffect, useCallback } from 'react';
import { useRouter } from '@atlassian/react-resource-router';
import { TABLE_DEFAULT_PAGE } from '../../common/ui/constants';
import type { PrioritySchemeFilters } from '../../services/priority-schemes-page/types';

const parsePriorityIds = (rawPriorityIds: string): PrioritySchemeFilters['priorityIds'] => {
	try {
		return rawPriorityIds
			.split(',')
			.map((id) => id.trim())
			.filter((id) => !!id);
	} catch (e) {
		return [];
	}
};

function buildFilters(rawPriorityIds: string, rawSchemeName: string): PrioritySchemeFilters {
	return {
		priorityIds: parsePriorityIds(rawPriorityIds),
		schemeName: rawSchemeName || '',
	};
}

/**
 * Hook to manage query params for the priority schemes page.
 * @param keys - list of query param keys
 * @returns a dictionary of query params and a function to update them
 */
export function useQueryParamsState(
	keys: string[],
): [Record<string, string>, (newValues: Record<string, string>) => void] {
	const [{ location }, { replace }] = useRouter();
	const [values, setValues] = useState<Record<string, string>>(() => {
		const params = new URLSearchParams(location.search);
		const initialValues: Record<string, string> = {};
		keys.forEach((key) => {
			const value = params.get(key);
			initialValues[key] = value ?? '';
		});
		return initialValues;
	});

	const setQueryParam = useCallback(
		(newValues: Record<string, string>) => {
			setValues((prevValues) => ({ ...prevValues, ...newValues }));
			const params = new URLSearchParams(location.search);
			Object.entries(newValues).forEach(([key, value]) => {
				if (value) {
					params.set(key, value);
				} else {
					// If the value is an empty string or otherwise falsy,
					// remove the parameter from the URL.
					params.delete(key);
				}
			});
			replace({ ...location, search: params.toString() });
		},
		[replace, location],
	);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const updatedValues: Record<string, string> = {};
		let shouldUpdateState = false;
		keys.forEach((key) => {
			const value = params.get(key) ?? '';
			if (values[key] !== value) {
				updatedValues[key] = value;
				shouldUpdateState = true;
			}
		});
		if (shouldUpdateState) {
			setValues((currentValues) => ({ ...currentValues, ...updatedValues }));
		}
		// ignoring values to avoid infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search, keys]);

	return [values, setQueryParam];
}

export const useFilters = () => {
	const [queryParams, setQueryParam] = useQueryParamsState([
		'pageNumber',
		'priorityId',
		'schemeName',
	]);
	const { priorityId, schemeName, pageNumber } = queryParams;

	const setSchemeNameWithReset = useCallback(
		(value: string) => {
			setQueryParam({ schemeName: value, pageNumber: TABLE_DEFAULT_PAGE.toString() });
		},
		[setQueryParam],
	);

	const setPriorityIdsFilter = useCallback(
		(ids: readonly string[]) => {
			setQueryParam({ priorityId: ids.join(',') });
		},
		[setQueryParam],
	);

	const filters = useMemo(
		() => buildFilters(priorityId || '', schemeName || ''),
		[priorityId, schemeName],
	);
	const pageNumberInt = useMemo(() => {
		const result = parseInt(pageNumber, 10);
		return Number.isNaN(result) ? TABLE_DEFAULT_PAGE : result;
	}, [pageNumber]);

	// we are using `useMemo` so that we don't provoke our consumers
	// to send unnecessary requests to the backend,
	const result = useMemo(
		() => ({
			filters,
			setSchemeName: setSchemeNameWithReset,
			setPriorityIdsFilter,
			pageNumber: pageNumberInt,
			setPageNumber: (value: Number) => setQueryParam({ pageNumber: value.toString() }),
		}),
		[filters, setSchemeNameWithReset, setPriorityIdsFilter, pageNumberInt, setQueryParam],
	);
	return result;
};
